import {
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    IconButton,
    PaletteColor,
    Tooltip,
    Typography,
} from '@mui/material';
import React from 'react';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FaOptionIcon } from '@europrocurement/l2d-icons';

export type CardItemAction = {
    label: string;
    icon: IconName;
    onClick: () => void;
};

export type CardItemProps = {
    title: string;
    icon: IconName;
    additionalInfos?: Array<string>;
    color?: PaletteColor;
    borderColor?: PaletteColor;
    onClick?: () => void;
    actions?: Array<CardItemAction>;
};

const CardItem: React.FunctionComponent<CardItemProps> = function (props) {
    const { title, icon, additionalInfos = [], color, borderColor, actions = [], onClick } = props;

    const handleClick = () => {
        if (onClick) {
            onClick();
        }
    };

    return (
        <Card
            data-testid="card-item"
            key={title}
            variant="outlined"
            sx={{
                margin: 0,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: borderColor ? borderColor.main : 'divider',
                cursor: onClick ? 'pointer' : 'default',
                '&:hover': onClick ? { boxShadow: 2 } : {},
            }}
            onClick={handleClick}
        >
            <CardContent sx={{ paddingTop: 0, paddingBottom: '0px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                        sx={{
                            backgroundColor: color ? color.light : 'grey.200',
                        }}
                    >
                        <FaOptionIcon
                            icon={['fasl', icon]}
                            sx={{
                                color: color ? color.dark : 'grey.600',
                                fontSize: '16px',
                            }}
                        />
                    </Avatar>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5em',
                            alignItems: 'flex-start',
                            flexGrow: 1,
                            maxWidth: '75%',
                        }}
                    >
                        <Typography
                            variant="l2dh7"
                            fontWeight="500"
                        >
                            {title}
                        </Typography>
                        {additionalInfos.length > 0 && (
                            <Box sx={{ display: 'flex', columnGap: '0.25em', flexWrap: 'wrap' }}>
                                {additionalInfos.map((item) => (
                                    <Chip
                                        key={item}
                                        label={item}
                                        color="default"
                                        size="small"
                                        sx={{
                                            mt: 0.5,
                                            backgroundColor: color ? color.light : 'text.grey25',
                                            color: color ? color.dark : 'text.grey75',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                        }}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                    <Box>
                        {actions.map((action) => (
                            <Tooltip
                                title={action.label}
                                key={action.label}
                            >
                                <IconButton
                                    sx={{
                                        display: 'flex',
                                        padding: '0',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: '25px',
                                        height: '25px',
                                    }}
                                    onClick={action.onClick}
                                >
                                    <FaOptionIcon
                                        icon={['fasl', action.icon]}
                                        color="text.gris35"
                                        sx={{
                                            fontWeight: '400',
                                            fontSize: '14px',
                                            lineHeight: '14px',
                                            ':hover': { color: 'text.secondary' },
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                        ))}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default CardItem;
